import { useEffect, useState, useContext } from "react";
import Logic from "../results-tracker/logic";
import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";
import { DataFetcher } from "../../utils/dataFetcher";
import throwMessage from "../../utils/throwMessage";
import { getJSON } from "../../utils/safeLocalStorage";
import { getCurrencyForSelectedAccount } from "../../utils/currencyUtils";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import moment from "moment";
import dayjs from "dayjs";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";

import CustomMultiTextField from "../../components/textfields/multiRow_standard";
import VoiceToText from "../../components/VoiceToText";
// import CustomDatePicker from "../../components/dateTimePickers/datePicker";
import { DataGrid } from "@mui/x-data-grid";
import columnsAll from "../../lib/columnsReflections";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Tooltip from "@mui/material/Tooltip";
import ConfirmDialog from "../../components/modals/confirm-dialog";

import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";

import Switch from "@mui/material/Switch";

import useWindowDimensions from "../../utils/width";
import {
  getAccountById,
  getSelectedAccountId,
} from "../../utils/accountUtils";

const getReflectionQuestions = (timeframe, reviewType) => {
  if (reviewType === "Weekly") {
    return {
      question_1: `What went well this week? What specific trades or decisions am I proud of?`,
      question_2: `What didn't go well this week? What mistakes did I make and what can I learn from them?`,
      question_3: `How was my emotional discipline this week? Did I follow my rules or let emotions take over?`,
      question_4: `Did I miss any trades this week? What were the reasons and how can I be better prepared next week?`,
      question_5: `What is the #1 thing I need to focus on improving next week?`,
    };
  }

  if (reviewType === "Monthly") {
    return {
      // Part 1: General Reflections
      question_1: `What did I accomplish this month? (How did I move the needle?)`,
      question_2: `What are the negatives/cons about the trades I took this month?`,
      question_3: `What are the positives/pros about the trades I took this month?`,
      question_4: `How do I bridge the gap between those weak points and strong points?`,
      question_5: `How do I feel about my work ethic? Do my actions align with my aspirations and goals?`,
      question_6: `How were my emotions when I was in trades? How were my emotions when I missed trades?`,
      question_7: `How do I feel about myself this month?`,
      question_8: `Things I will do more of:`,
      question_9: `Things I will do less of:`,
      question_10: `Anything else I want to add (other areas of my life, things I am grateful for, etc.)`,

      // Part 2: How to Miss Less Trades Next Month
      question_11: `Common reason: What can I do to protect against these in the future?`,
      question_12: `2nd Common reason: What can I do to protect against these in the future?`,
      question_13: `3rd Common reason: What can I do to protect against these in the future?`,
    };
  }

  if (
    reviewType === "Quarterly" ||
    reviewType === "Q1" ||
    reviewType === "Q2" ||
    reviewType === "Q3" ||
    reviewType === "Q4"
  ) {
    return {
      // Part 1: Technical SWOT Analysis
      question_1: `What are your greatest technical strengths?`,
      question_2: `What are your worst technical weaknesses?`,
      question_3: `What are your greatest opportunities to grow in your technicals?`,
      question_4: `What do you think are the greatest threats to your technicals? (i.e., the technical traps you usually fall into that cause you to give back the most profit)`,

      // Part 2: Psychological SWOT Analysis
      question_5: `What are your greatest psychological strengths?`,
      question_6: `What are your worst psychological weaknesses?`,
      question_7: `What are your greatest opportunities to grow in your mindset?`,
      question_8: `What do you think are the greatest threats to your trading psychology? (i.e., the mindset or emotional traps you usually fall into that cause you to give back the most profit)`,

      // Part 3: Deep Dive Performance Review
      question_9: `90-Day Plan Evaluation: Look back at your 90-Day Plan from this quarter. Did you achieve everything you set out to achieve? Detail the reasons why you fell short, matched, or exceeded your expectations.`,
      question_10: `Work Ethic & Alignment: How do I feel about my work ethic? Do my actions truly align with my aspirations and goals?`,
      question_11: `Defining Success: What do I absolutely have to achieve this year in order for me to consider it a success?`,
      question_12: `Things I will do more of:`,
      question_13: `Things I will do less of:`,
      question_14: `Anything else I want to add (other areas of my life, things I am grateful for, etc.)`,
    };
  }

  if (reviewType === "Annual") {
    return {};
  }

  // Default fallback
  return {
    question_1: `Did I execute 100% according to my trading plan this ${timeframe}? (What did I do well, and where did I deviate?)`,
    question_2: `Based on this ${timeframe}'s data, what is one specific habit I need to double down on, and one specific mistake I must eliminate next ${timeframe}?`,
    question_3: `Rate my emotional discipline on a scale of 1-10 when managing active trades and after missed trades during this ${timeframe}. Did I chase the market, or did I accept the missed opportunity?`,
    question_4: `Honestly evaluate this ${timeframe}'s work ethic: Did my daily preparation, routine, and discipline match the standard of the successful trader I intend to become?`,
    question_5: `Outside of the charts, what external factors affected my trading this ${timeframe} (sleep, health, environment)? What am I genuinely grateful for?`,
  };
};

const SingleReflection = ({
  setSingleReflectionView,
  handleScroll,
  setSingleJournalView,
  setOpenJournal,
  setSingleForecastView,
  handleCloseNewReflection,
}) => {
  const { width } = useWindowDimensions();
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Use state for selectedAccountId so it updates when changed
  const [selectedAccountId, setSelectedAccountIdState] = useState(
    getSelectedAccountId(),
  );

  // Listen for account changes
  useEffect(() => {
    const handleAccountChanged = () => {
      setSelectedAccountIdState(getSelectedAccountId());
    };

    window.addEventListener("account-changed", handleAccountChanged);
    return () =>
      window.removeEventListener("account-changed", handleAccountChanged);
  }, []);
  const [date, setDate] = useState(dayjs(new Date()));
  const [id, setId] = useState("");
  const [typeOfReview, setTypeOfReview] = useState("");
  const [question_1, setQuestion_1] = useState("");
  const [question_2, setQuestion_2] = useState("");
  const [question_3, setQuestion_3] = useState("");
  const [question_4, setQuestion_4] = useState("");
  const [question_5, setQuestion_5] = useState("");
  const [question_6, setQuestion_6] = useState("");
  const [question_7, setQuestion_7] = useState("");
  const [question_8, setQuestion_8] = useState("");
  const [question_9, setQuestion_9] = useState("");
  const [question_10, setQuestion_10] = useState("");
  const [question_11, setQuestion_11] = useState("");
  const [question_12, setQuestion_12] = useState("");
  const [question_13, setQuestion_13] = useState("");
  const [question_14, setQuestion_14] = useState("");
  const [question_15, setQuestion_15] = useState("");
  const [question_16, setQuestion_16] = useState("");
  const [question_17, setQuestion_17] = useState("");
  const [question_18, setQuestion_18] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Weekly question sub-sections
  const [weeklyQ1_wentWell, setWeeklyQ1_wentWell] = useState("");
  const [weeklyQ1_proudOf, setWeeklyQ1_proudOf] = useState("");
  const [weeklyQ2_didntGoWell, setWeeklyQ2_didntGoWell] = useState("");
  const [weeklyQ2_mistakes, setWeeklyQ2_mistakes] = useState("");
  const [weeklyQ2_learnings, setWeeklyQ2_learnings] = useState("");
  const [weeklyQ3_emotionalDiscipline, setWeeklyQ3_emotionalDiscipline] = useState("");
  const [weeklyQ3_followedRules, setWeeklyQ3_followedRules] = useState("");
  const [weeklyQ4_missedTrades, setWeeklyQ4_missedTrades] = useState("");
  const [weeklyQ4_reasons, setWeeklyQ4_reasons] = useState("");
  const [weeklyQ4_betterPrepared, setWeeklyQ4_betterPrepared] = useState("");
  // Monthly question sub-sections
  const [monthlyQ1_accomplished, setMonthlyQ1_accomplished] = useState("");
  const [monthlyQ1_movedNeedle, setMonthlyQ1_movedNeedle] = useState("");
  const [monthlyQ5_workEthic, setMonthlyQ5_workEthic] = useState("");
  const [monthlyQ5_alignActions, setMonthlyQ5_alignActions] = useState("");
  const [monthlyQ6_emotionsInTrades, setMonthlyQ6_emotionsInTrades] = useState("");
  const [monthlyQ6_emotionsMissed, setMonthlyQ6_emotionsMissed] = useState("");
  const [monthlyQ10_otherAreas, setMonthlyQ10_otherAreas] = useState("");
  const [monthlyQ10_grateful, setMonthlyQ10_grateful] = useState("");
  const [monthlyQ11_reason, setMonthlyQ11_reason] = useState("");
  const [monthlyQ11_protect, setMonthlyQ11_protect] = useState("");
  const [monthlyQ12_reason, setMonthlyQ12_reason] = useState("");
  const [monthlyQ12_protect, setMonthlyQ12_protect] = useState("");
  const [monthlyQ13_reason, setMonthlyQ13_reason] = useState("");
  const [monthlyQ13_protect, setMonthlyQ13_protect] = useState("");
  // Quarterly question sub-sections
  const [quarterlyQ9_achieved, setQuarterlyQ9_achieved] = useState("");
  const [quarterlyQ9_reasons, setQuarterlyQ9_reasons] = useState("");
  const [quarterlyQ10_workEthic, setQuarterlyQ10_workEthic] = useState("");
  const [quarterlyQ10_alignActions, setQuarterlyQ10_alignActions] = useState("");
  const [quarterlyQ14_otherAreas, setQuarterlyQ14_otherAreas] = useState("");
  const [quarterlyQ14_grateful, setQuarterlyQ14_grateful] = useState("");
  const [q1, setQ1] = useState(undefined);
  const [q2, setQ2] = useState(undefined);
  const [q3, setQ3] = useState(undefined);
  const [q4, setQ4] = useState(undefined);
  const [weekly, setWeekly] = useState(undefined);
  const [monthly, setMonthly] = useState(undefined);
  const [Annual, setAnnual] = useState(undefined);
  const [hideTypeOf, setHideTypeOf] = useState(true);
  // const [changeDate, setChangeDate] = useState(false);
  const [trades, setTrades] = useState([]);

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [finding, setFinidng] = useState(true);
  const [missedTrades, setMissedTrades] = useState(false);

  const [isHoveringNext, setIsHoveringNext] = useState(false);
  const [isHoveringBack, setIsHoveringBack] = useState(false);
  const [isHoveringShare, setIsHoveringShare] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const [returnPercentPC, setReturnPercentPC] = useState(0);
  const [returnPercentIC, setReturnPercentIC] = useState(0);
  const [returnPercentPCNet, setReturnPercentPCNet] = useState(0);
  const [returnPercentICNet, setReturnPercentICNet] = useState(0);
  const [returnDollarPersonalGross, setReturnDollarPersonalGross] = useState(0);
  const [returnDollarInvestorGross, setReturnDollarInvestorGross] = useState(0);
  const [returnDollarPersonalNet, setReturnDollarPersonalNet] = useState(0);
  const [returnDollarInvestorNet, setReturnDollarInvestorNet] = useState(0);
  const [SingleReflection, setSingleReflection] = useState(null);
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross") === "true");

  const [winsPC, setWinsPC] = useState(0);
  const [lossPC, setLossPC] = useState(0);
  const [inProgressPC, setInProgressPC] = useState(0);
  const [winsIC, setWinsIC] = useState(0);
  const [lossIC, setLossIC] = useState(0);
  const [inProgressIC, setInProgressIC] = useState(0);
  const [bePC, setBePC] = useState(0);
  const [beIC, setBeIC] = useState(0);

  const [lowRiskPC, setLowRiskPC] = useState(0);
  const [highRiskPC, setHighRiskPC] = useState(0);
  const [invalidPC, setInvalidPC] = useState(0);
  const [lowRiskIC, setLowRiskIC] = useState(0);
  const [highRiskIC, setHighRiskIC] = useState(0);
  const [invalidIC, setInvalidIC] = useState(0);

  const mode = localStorage.getItem("equity-mode");

  // Listen for gross-net-changed events from dashboard
  useEffect(() => {
    const handleGrossNetChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    window.addEventListener("gross-net-changed", handleGrossNetChanged);
    
    return () => {
      window.removeEventListener("gross-net-changed", handleGrossNetChanged);
    };
  }, []);

  // Determine display mode based on selectedAccountId (not legacy mode)
  const getDisplayMode = () => {
    if (selectedAccountId === "All") return "All";
    if (selectedAccountId === "All Personal") return "Personal Capital";
    if (selectedAccountId === "All Investor") return "Investor Capital";
    if (selectedAccountId?.startsWith("pc-")) return "Personal Capital";
    if (selectedAccountId?.startsWith("ic-")) return "Investor Capital";
    return mode; // fallback to legacy
  };

  const displayMode = getDisplayMode();

  const getTimeframeText = () => {
    if (weekly) return "week";
    if (monthly) return "month";
    if (Annual) return "year";
    if (q1 || q2 || q3 || q4) return "quarter";
    return "";
  };

  const getCurrentReviewType = () => {
    if (weekly) return "Weekly";
    if (monthly) return "Monthly";
    if (Annual) return "Annual";
    if (q1) return "Q1";
    if (q2) return "Q2";
    if (q3) return "Q3";
    if (q4) return "Q4";
    return "";
  };

  // Load account data when selectedAccountId changes - same as statsbar
  useEffect(() => {
    const loadAccount = async () => {
      if (
        selectedAccountId &&
        selectedAccountId !== "All" &&
        selectedAccountId !== "All Personal" &&
        selectedAccountId !== "All Investor"
      ) {
        try {
          const account = await getAccountById(selectedAccountId);
          setSelectedAccount(account);
        } catch (error) {
          console.error("Error loading account:", error);
          setSelectedAccount(null);
        }
      } else {
        setSelectedAccount(null);
      }
    };

    loadAccount();
  }, [selectedAccountId]);

  Logic();

  // Get currency symbol
  const getCurrency = () => {
    const user = getJSON("user", {});
    return getCurrencyForSelectedAccount(selectedAccountId, selectedAccount, user, true);
  };

  // Calculate dynamic rows based on content
  const calculateRows = (text) => {
    if (!text || text.trim() === "") return 3;
    const lineCount = text.split("\n").length;
    const charCount = text.length;
    // Estimate rows based on characters (roughly 80 chars per row)
    const estimatedRows = Math.ceil(charCount / 80);
    return Math.max(3, Math.max(lineCount, estimatedRows));
  };

  const handleSave = async () => {
    const schema = {
      typeOfReview: typeOfReview,
      date: date,
      question_1: question_1,
      question_2: question_2,
      question_3: question_3,
      question_4: question_4,
      question_5: question_5,
      question_6: question_6,
      question_7: question_7,
      question_8: question_8,
      question_9: question_9,
      question_10: question_10,
      question_11: question_11,
      question_12: question_12,
      question_13: question_13,
      question_14: question_14,
      question_15: question_15,
      question_16: question_16,
      question_17: question_17,
      question_18: question_18,
      _id: id,
      // Weekly sub-sections
      weeklyQ1_wentWell: weeklyQ1_wentWell,
      weeklyQ1_proudOf: weeklyQ1_proudOf,
      weeklyQ2_didntGoWell: weeklyQ2_didntGoWell,
      weeklyQ2_mistakes: weeklyQ2_mistakes,
      weeklyQ2_learnings: weeklyQ2_learnings,
      weeklyQ3_emotionalDiscipline: weeklyQ3_emotionalDiscipline,
      weeklyQ3_followedRules: weeklyQ3_followedRules,
      weeklyQ4_missedTrades: weeklyQ4_missedTrades,
      weeklyQ4_reasons: weeklyQ4_reasons,
      weeklyQ4_betterPrepared: weeklyQ4_betterPrepared,
      // Monthly sub-sections
      monthlyQ1_accomplished: monthlyQ1_accomplished,
      monthlyQ1_movedNeedle: monthlyQ1_movedNeedle,
      monthlyQ5_workEthic: monthlyQ5_workEthic,
      monthlyQ5_alignActions: monthlyQ5_alignActions,
      monthlyQ6_emotionsInTrades: monthlyQ6_emotionsInTrades,
      monthlyQ6_emotionsMissed: monthlyQ6_emotionsMissed,
      monthlyQ10_otherAreas: monthlyQ10_otherAreas,
      monthlyQ10_grateful: monthlyQ10_grateful,
      monthlyQ11_reason: monthlyQ11_reason,
      monthlyQ11_protect: monthlyQ11_protect,
      monthlyQ12_reason: monthlyQ12_reason,
      monthlyQ12_protect: monthlyQ12_protect,
      monthlyQ13_reason: monthlyQ13_reason,
      monthlyQ13_protect: monthlyQ13_protect,
      // Quarterly sub-sections
      quarterlyQ9_achieved: quarterlyQ9_achieved,
      quarterlyQ9_reasons: quarterlyQ9_reasons,
      quarterlyQ10_workEthic: quarterlyQ10_workEthic,
      quarterlyQ10_alignActions: quarterlyQ10_alignActions,
      quarterlyQ14_otherAreas: quarterlyQ14_otherAreas,
      quarterlyQ14_grateful: quarterlyQ14_grateful,
    };
    if (!formSubmitted);
    setFormSubmitted(true);
    const result = await DataFetcher("edit-reflection", schema);
    if (result) {
      const filteredArray = auth.reflections.filter((x) => x._id !== id);
      filteredArray.push(result);
      auth.setReflections(filteredArray);
      throwMessage("success", "Success");
      setFormSubmitted(false);
    } else {
      setFormSubmitted(false);
      throwMessage("error", "Something went wrong");
    }
  };

  // NOTE: Calculation logic has been moved to server-side centralized service
  // All calculations now happen in /server/lib/tradeCalculations.js
  // Trades use pre-calculated DB fields for performance

  const runDurationCal = (params) => {
    // Use pre-calculated duration from database (in hours)
    const hours = params.row.duration || 0;
    
    if (hours === 0) {
      return <span>-</span>;
    }

    const days = (hours / 24).toFixed(1);

    if (!isNaN(hours)) {
      return (
        <p>
          {days > 1 ? days + "d" : hours.toFixed(1) + "h"}
        </p>
      );
    } else {
      return <span>-</span>;
    }
  };

  const runCalculations = async (_trades) => {
    let trades = _trades;
    let virtualArray = [];

    let totalOutcomesPC = [];
    let totalOutcomesIC = [];

    let returnPercentTotalsPC = [];
    let returnPercentTotalsIC = [];
    let returnDollarTotalsPersonal = [];
    let returnDollarTotalsInvestor = [];
    let allFeesIC = [];
    let allFeesPC = [];

    // Filter by selected account - use component state, not localStorage
    const currentAccountId = getSelectedAccountId();

    if (currentAccountId && currentAccountId !== "All") {
      if (currentAccountId === "All Personal") {
        trades = trades.filter(
          (t) =>
            t?.equityType === "Personal Capital" ||
            t?.equityType === "Backtest" ||
            (!t?.equityType && t?.id?.includes("backtest-")),
        );
      } else if (currentAccountId === "All Investor") {
        trades = trades.filter((t) => t?.equityType === "Investor Capital");
      } else {
        // Filter by specific account ID
        trades = trades.filter((t) => t?.accountId === currentAccountId);
      }
    }

    for (let index = 0; index < trades.length; index++) {
      const trade = trades[index];
      const capital = trade?.equityType;

      if (capital === "Investor Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippageIC = trade.outcomeIC;
        const profitLossPercentageIC = parseFloat(trade.profitLossPercentageIC || 0);
        const profitLossDollarInvestor = parseFloat(trade.profitLossDollarInvestor || 0);

        allFeesIC.push(parseFloat(trade.feeIC) + parseFloat(trade.swapIC || 0));

        returnPercentTotalsIC.push(profitLossPercentageIC);
        returnDollarTotalsInvestor.push(profitLossDollarInvestor);
        totalOutcomesIC.push(outcomeOfSlippageIC);

        // Use DB-calculated percentWithFeesIC (already calculated by server)
        const percentWithFeesIC = parseFloat(trade.percentWithFeesIC || 0);

        const schema = {
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          // riskValue: parseFloat(riskValue.toFixed(2)),
          // valuePerPip: parseFloat(valuePerPip.toFixed(2)),
          profitLossDollarInvestor: trade.profitLossDollarInvestor,
          profitLossDollarPersonal: 0,
          profitLossPercentagePC: undefined,
          profitLossPercentageIC: parseFloat(profitLossPercentageIC).toFixed(2),
          percentWithFeesPC: undefined,
          percentWithFeesIC: parseFloat(percentWithFeesIC).toFixed(2),
          // rr: riskToRewardDecimal.toFixed(2),
          outcomePC: "",
          outcomeIC: outcomeOfSlippageIC,
          openPricePC: 0,
          closePricePC: 0,

          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          direction: trade.direction,
          stopLossPC: 0,
          stopLossPricePC: 0,
          stopLossIC: trade.stopLossIC,
          stopLossPriceIC: trade.stopLossPriceIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          duration: "",
          foreCastId: trade.foreCastId,
          
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          returnTotalsPersonal: 0,
          returnTotalsInvestor: parseFloat(trade.returnTotalsInvestor || 0).toFixed(2),
          created: trade.created,
          equityType: trade?.equityType,
          lotsPC: trade.lotsPC,
          lotsIC: trade.lotsIC,
          unitsPC: trade.unitsPC,
          unitsIC: trade.unitsIC,
          moneyPC: trade.moneyPC,
          moneyIC: trade.moneyIC,
          risk: trade.risk,
          duplicatedFrom: trade.duplicatedFrom,
          accountId: trade.accountId,
        };
        virtualArray.push(schema);
      } else if (capital === "Personal Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const profitLossPercentagePC = parseFloat(trade.profitLossPercentagePC || 0);
        const profitLossDollarPersonal = parseFloat(trade.profitLossDollarPersonal || 0);

        allFeesPC.push(parseFloat(trade.feePC) + parseFloat(trade.swapPC || 0));

        returnPercentTotalsPC.push(profitLossPercentagePC);
        returnDollarTotalsPersonal.push(profitLossDollarPersonal);

        totalOutcomesPC.push(outcomeOfSlippagePC);

        // Use DB-calculated percentWithFeesPC (already calculated by server)
        const percentWithFeesPC = parseFloat(trade.percentWithFeesPC || 0);

        const schema = {
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: trade.profitLossDollarInvestor,
          profitLossDollarPersonal: trade.profitLossDollarPersonal,
          profitLossPercentagePC: parseFloat(profitLossPercentagePC).toFixed(2),
          profitLossPercentageIC: 0,
          percentWithFeesPC: parseFloat(percentWithFeesPC).toFixed(2),
          percentWithFeesIC: undefined,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: "",
          openPricePC: trade.openPricePC,
          closePricePC: trade.closePricePC,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          direction: trade.direction,
          stopLossPC: trade.stopLossPC,
          stopLossIC: 0,
          stopLossPricePC: trade.stopLossPricePC,
          stopLossPriceIC: trade.stopLossPriceIC,

          entryDate: trade.entryDate,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          duration: "",
          foreCastId: trade.foreCastId,
          
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          returnTotalsPersonal: parseFloat(trade.returnTotalsPersonal || 0).toFixed(2),
          returnTotalsInvestor: 0,
          created: trade.created,
          equityType: trade?.equityType,
          lotsPC: trade.lotsPC,
          lotsIC: trade.lotsIC,
          unitsPC: trade.unitsPC,
          unitsIC: trade.unitsIC,
          moneyPC: trade.moneyPC,
          moneyIC: trade.moneyIC,
          risk: trade.risk,
          duplicatedFrom: trade.duplicatedFrom,
          accountId: trade.accountId,
        };
        virtualArray.push(schema);
      } else {
        // Handle backtest trades or trades with other equity types
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const profitLossPercentagePC = parseFloat(trade.profitLossPercentagePC || 0);
        const profitLossDollarPersonal = parseFloat(trade.profitLossDollarPersonal || 0);

        allFeesPC.push(parseFloat(trade.feePC || 0) + parseFloat(trade.swapPC || 0));

        returnPercentTotalsPC.push(profitLossPercentagePC);
        returnDollarTotalsPersonal.push(profitLossDollarPersonal);

        totalOutcomesPC.push(outcomeOfSlippagePC);

        // Use DB-calculated percentWithFeesPC (already calculated by server)
        const percentWithFeesPC = parseFloat(trade.percentWithFeesPC || 0);

        const schema = {
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: trade.profitLossDollarInvestor,
          profitLossDollarPersonal: trade.profitLossDollarPersonal,
          profitLossPercentagePC: parseFloat(profitLossPercentagePC).toFixed(2),
          profitLossPercentageIC: undefined,
          percentWithFeesPC: parseFloat(percentWithFeesPC).toFixed(2),
          percentWithFeesIC: undefined,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: "",
          openPricePC: trade.openPricePC,
          closePricePC: trade.closePricePC,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          direction: trade.direction,
          stopLossIC: 0,
          stopLossPC: trade.stopLossPC,
          stopLossPricePC: trade.stopLossPricePC,
          stopLossPriceIC: trade.stopLossPriceIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          duration: "",
          foreCastId: trade.foreCastId,
          
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          returnTotalsPersonal: parseFloat(trade.returnTotalsPersonal || 0).toFixed(2),
          returnTotalsInvestor: 0,
          created: trade.created,
          equityType: trade?.equityType || "Backtest",
          lotsPC: trade.lotsPC,
          lotsIC: trade.lotsIC,
          unitsPC: trade.unitsPC,
          unitsIC: trade.unitsIC,
          moneyPC: trade.moneyPC,
          moneyIC: trade.moneyIC,
          risk: trade.risk,
          duplicatedFrom: trade.duplicatedFrom,
          accountId: trade.accountId,
        };
        virtualArray.push(schema);
      }
    }

    // Get starting balances from capitalAccounts
    const user = getJSON("user", {});
    const accounts = user.capitalAccounts || [];
    
    // Aggregate starting balances by account type
    let startingBalancePC = 0;
    let startingBalanceIC = 0;
    
    accounts.forEach(account => {
      if (account.isActive) {
        const balance = parseFloat(account.startingBalance) || 0;
        if (account.type === "Personal") {
          startingBalancePC += balance;
        } else if (account.type === "Investor") {
          startingBalanceIC += balance;
        }
      }
    });
    
    // Fallback to legacy fields if no accounts found
    if (startingBalancePC === 0) {
      startingBalancePC = parseFloat(user.startingBalancePC) || 0;
    }
    if (startingBalanceIC === 0) {
      startingBalanceIC = parseFloat(user.startingBalanceIC) || 0;
    }
    
    const winsPC = totalOutcomesPC.filter((res) => res === "Win");
    const lossesPC = totalOutcomesPC.filter((res) => res === "Loss");
    const breakevensPC = totalOutcomesPC.filter((res) => res === "Breakeven");
    const inProgressPC = totalOutcomesPC.filter((res) => res === "In Progress");

    const winsIC = totalOutcomesIC.filter((res) => res === "Win");
    const lossesIC = totalOutcomesIC.filter((res) => res === "Loss");
    const breakevensIC = totalOutcomesIC.filter((res) => res === "Breakeven");
    const inProgressIC = totalOutcomesIC.filter((res) => res === "In Progress");

    setInProgressPC(inProgressPC.length);
    setBePC(breakevensPC.length);
    setLossPC(lossesPC.length);
    setWinsPC(winsPC.length);

    setInProgressIC(inProgressIC.length);
    setBeIC(breakevensIC.length);
    setLossIC(lossesIC.length);
    setWinsIC(winsIC.length);

    // Calculate risk buckets for Personal Capital trades
    const lowRiskPC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Personal Capital" && trade.grade === "Low Risk",
    );
    const highRiskPC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Personal Capital" && trade.grade === "High Risk",
    );
    const invalidPC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Personal Capital" && trade.grade === "Invalid",
    );

    // Calculate risk buckets for Investor Capital trades
    const lowRiskIC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Investor Capital" && trade.grade === "Low Risk",
    );
    const highRiskIC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Investor Capital" && trade.grade === "High Risk",
    );
    const invalidIC = virtualArray.filter(
      (trade) =>
        trade.equityType === "Investor Capital" && trade.grade === "Invalid",
    );

    // Also include backtest trades in Personal Capital risk buckets
    const lowRiskPCBacktest = virtualArray.filter(
      (trade) =>
        (trade.equityType === "Backtest" || !trade.equityType) &&
        trade.grade === "Low Risk",
    );
    const highRiskPCBacktest = virtualArray.filter(
      (trade) =>
        (trade.equityType === "Backtest" || !trade.equityType) &&
        trade.grade === "High Risk",
    );
    const invalidPCBacktest = virtualArray.filter(
      (trade) =>
        (trade.equityType === "Backtest" || !trade.equityType) &&
        trade.grade === "Invalid",
    );

    setLowRiskPC(lowRiskPC.length + lowRiskPCBacktest.length);
    setHighRiskPC(highRiskPC.length + highRiskPCBacktest.length);
    setInvalidPC(invalidPC.length + invalidPCBacktest.length);
    setLowRiskIC(lowRiskIC.length);
    setHighRiskIC(highRiskIC.length);
    setInvalidIC(invalidIC.length);

    // Calculate totals from trades in virtualArray (reflection-specific trades only)
    let totalProfitLossPC = 0;
    let totalProfitLossIC = 0;
    let totalFeesPC = 0;
    let totalFeesIC = 0;

    virtualArray.forEach(trade => {
      if (trade.equityType === "Personal Capital" || trade.equityType === "Backtest") {
        totalProfitLossPC += parseFloat(trade.profitLossDollarPersonal || 0);
        totalFeesPC += parseFloat(trade.feePC || 0) + parseFloat(trade.swapPC || 0);
      } else if (trade.equityType === "Investor Capital") {
        totalProfitLossIC += parseFloat(trade.profitLossDollarInvestor || 0);
        totalFeesIC += parseFloat(trade.feeIC || 0) + parseFloat(trade.swapIC || 0);
      }
    });

    // Gross = profit/loss only, Net = profit/loss + fees
    const returnDollarTotals_Personal_gross = totalProfitLossPC;
    const returnDollarTotals_Investor_gross = totalProfitLossIC;
    const returnDollarTotals_Personal_net = totalProfitLossPC + totalFeesPC;
    const returnDollarTotals_Investor_net = totalProfitLossIC + totalFeesIC;

    // Calculate percentages by summing individual trade percentages (not by dividing by starting balance)
    // This matches the individual % Gross shown in the table
    const sumTotalProfitLossPercentagePC = returnPercentTotalsPC.reduce((a, b) => a + b, 0);
    const sumTotalProfitLossPercentageIC = returnPercentTotalsIC.reduce((a, b) => a + b, 0);

    // For net percentages, calculate from dollar totals and starting balance
    const sumTotalProfitLossPercentagePCNet = startingBalancePC !== 0
      ? (returnDollarTotals_Personal_net / startingBalancePC) * 100
      : 0;

    const sumTotalProfitLossPercentageICNet = startingBalanceIC !== 0
      ? (returnDollarTotals_Investor_net / startingBalanceIC) * 100
      : 0;

    setReturnDollarInvestorGross(parseFloat(returnDollarTotals_Investor_gross));
    setReturnDollarPersonalGross(parseFloat(returnDollarTotals_Personal_gross));
    setReturnDollarInvestorNet(parseFloat(returnDollarTotals_Investor_net));
    setReturnDollarPersonalNet(parseFloat(returnDollarTotals_Personal_net));

    setReturnPercentIC(parseFloat(sumTotalProfitLossPercentageIC));
    setReturnPercentPC(parseFloat(sumTotalProfitLossPercentagePC));
    setReturnPercentICNet(parseFloat(sumTotalProfitLossPercentageICNet));
    setReturnPercentPCNet(parseFloat(sumTotalProfitLossPercentagePCNet));

    return virtualArray;
  };
  const getTrades = async (date, typeOfReview, testId) => {
    let result;

    if (auth.backtestingMode && typeOfReview === "Backtest") {
      // Handle backtest mode - filter trades by typeOfReview and backtest trades
      result = auth.trades.filter((trade) => trade.id?.includes("backtest-") && trade.testId === testId);
    } else {
      // Handle live mode - get trades by time period
      result = await DataFetcher("get-trades-to-reflect", {
        date: date,
        typeOfReviewQuery: typeOfReview,
        missedTrade: missedTrades ? true : false,
      });
    }

    if (result) {
      let _trades = [];
      if (!missedTrades) {
        if (auth.backtestingMode) {
          _trades = result.filter((trades) => trades.id?.includes("backtest-") && trades.testId === testId);
        } else {
          _trades = result.filter((trades) => trades.id?.includes("live-"));
        }
      } else {
        _trades = result.filter((trades) =>
          trades.missedTrade === true,
        );
      }

      const calTrades = await runCalculations(_trades);
      setTrades(calTrades.reverse());
    }
  };
  const goBack = () => {
    setSingleReflectionView(false);
    setSingleJournalView(false);
    setOpenJournal(false);
    // handleScroll();
    localStorage.removeItem("reflection-to-view");
  };

  const handleShare = async () => {
    if (isSharing) return;

    setIsSharing(true);

    try {
      const schema = {
        _id: id,
        accountId: selectedAccountId,
        missedTrades: missedTrades,
        backtestMode:
          auth.backtestingMode ||
          localStorage.getItem("backtest-mode") === "true",
      };

      const result = await DataFetcher(
        "generate-reflection-share-token",
        schema,
      );

      if (result && result.slug) {
        const typeOfReviewLower = typeOfReview
          ? typeOfReview.toLowerCase().replace(/\s+/g, "-")
          : "unknown";
        const dateSlug = result.slug.includes("/")
          ? result.slug.split("/")[1]
          : result.slug;
        const shareUrl = `${window.location.origin}/reflection/${typeOfReviewLower}/${dateSlug}`;

        await navigator.clipboard.writeText(shareUrl);
        auth.throwMessage("success", "Share link copied to clipboard");
      } else {
        auth.throwMessage("error", "Failed to generate share link");
      }
    } catch (error) {
      auth.throwMessage("error", "Error sharing reflection");
    } finally {
      setIsSharing(false);
    }
  };

  const handleQ1 = (event) => {
    setQ1(event.target.value);
    setQ2("");
    setQ3("");
    setQ4("");
    setWeekly("");
    setMonthly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
  };
  const handleQ2 = (event) => {
    setQ1("");
    setQ2(event.target.value);
    setQ3("");
    setQ4("");
    setWeekly("");
    setMonthly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
  };
  const handleQ3 = (event) => {
    setQ1("");
    setQ2("");
    setQ3(event.target.value);
    setQ4("");
    setWeekly("");
    setMonthly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
  };
  const handleQ4 = (event) => {
    setQ1("");
    setQ2("");
    setQ3("");
    setQ4(event.target.value);
    setWeekly("");
    setMonthly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
  };
  const handleWeekly = (event) => {
    setWeekly(event.target.value);
    setMonthly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
    setQ1("");
    setQ2("");
    setQ3("");
    setQ4("");
  };
  const handleMonthly = (event) => {
    setMonthly(event.target.value);
    setWeekly("");
    setAnnual("");
    setTrades([]);
    setHideTypeOf(true);
    setQ1("");
    setQ2("");
    setQ3("");
    setQ4("");
  };
  const handleAnnual = (event) => {
    setAnnual(event.target.value);
    setMonthly("");
    setWeekly("");
    setTrades([]);
    setHideTypeOf(true);
    setQ1("");
    setQ2("");
    setQ3("");
    setQ4("");
  };
  const deleteReflections = async () => {
    const schema = {
      _id: localStorage.getItem("reflection-to-view"),
    };
    const result = await DataFetcher("delete-reflection", schema);
    if (result) {
      const filteredArray = auth.reflections.filter(
        (x) => x._id !== localStorage.getItem("reflection-to-view"),
      );
      auth.setReflections(filteredArray);
      throwMessage("success", "Deleted");
      goBack();
    }
  };
  const handleMissedTrades = (e) => {
    setMissedTrades(e.target.checked);
  };

  const setItPercent = (mode, returnPercentPC, returnPercentIC) => {
    const pcic =
      returnPercentPC + returnPercentIC < 0
        ? "#f23645"
        : returnPercentPC + returnPercentIC > 0
          ? "#26a65d"
          : "#ED9E0E";

    const pc =
      returnPercentPC < 0
        ? "#f23645"
        : returnPercentPC > 0
          ? "#26a65d"
          : "#ED9E0E";

    const ic =
      returnPercentIC < 0
        ? "#f23645"
        : returnPercentIC > 0
          ? "#26a65d"
          : "#ED9E0E";

    if (mode === "Personal Capital") {
      return pc;
    } else if (mode === "Investor Capital") {
      return ic;
    } else {
      return pcic;
    }
  };

  const setItDollar = (mode, returnDollarPersonal, returnDollarInvestor) => {
    const pcic =
      returnDollarPersonal + returnDollarInvestor < 0
        ? "#f23645"
        : returnDollarPersonal + returnDollarInvestor > 0
          ? "#26a65d"
          : "#ED9E0E";

    const pc =
      returnDollarPersonal < 0
        ? "#f23645"
        : returnDollarPersonal > 0
          ? "#26a65d"
          : "#ED9E0E";

    const ic =
      returnDollarInvestor < 0
        ? "#f23645"
        : returnDollarInvestor > 0
          ? "#26a65d"
          : "#ED9E0E";

    if (mode === "Personal Capital") {
      return pc;
    } else if (mode === "Investor Capital") {
      return ic;
    } else {
      return pcic;
    }
  };

  useEffect(() => {
    if (typeOfReview) getTrades(date, typeOfReview, SingleReflection?.testId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, missedTrades, selectedAccountId, SingleReflection?.testId]);

  const findReflection = () => {
    const reflection = auth.reflections.find((reflection) =>
      reflection?._id.includes(localStorage.getItem("reflection-to-view")),
    );
    setSingleReflection(reflection);
    if (reflection) {
      setDate(reflection.date);
      getTrades(reflection.date, reflection.typeOfReview, reflection.testId);
      setTypeOfReview(reflection.typeOfReview);
      setQuestion_1(reflection.question_1);
      setQuestion_2(reflection.question_2);
      setQuestion_3(reflection.question_3);
      setQuestion_4(reflection.question_4);
      setQuestion_5(reflection.question_5);
      setQuestion_6(reflection.question_6);
      setQuestion_7(reflection.question_7);
      setQuestion_8(reflection.question_8);
      setQuestion_9(reflection.question_9);
      setQuestion_10(reflection.question_10);
      setQuestion_11(reflection.question_11);
      setQuestion_12(reflection.question_12);
      setQuestion_13(reflection.question_13);
      setQuestion_14(reflection.question_14);
      setQuestion_15(reflection.question_15);
      setQuestion_16(reflection.question_16);
      setQuestion_17(reflection.question_17);
      setQuestion_18(reflection.question_18);
      setId(reflection._id);
      // Load sub-sections
      setWeeklyQ1_wentWell(reflection.weeklyQ1_wentWell || "");
      setWeeklyQ1_proudOf(reflection.weeklyQ1_proudOf || "");
      setWeeklyQ2_didntGoWell(reflection.weeklyQ2_didntGoWell || "");
      setWeeklyQ2_mistakes(reflection.weeklyQ2_mistakes || "");
      setWeeklyQ2_learnings(reflection.weeklyQ2_learnings || "");
      setWeeklyQ3_emotionalDiscipline(reflection.weeklyQ3_emotionalDiscipline || "");
      setWeeklyQ3_followedRules(reflection.weeklyQ3_followedRules || "");
      setWeeklyQ4_missedTrades(reflection.weeklyQ4_missedTrades || "");
      setWeeklyQ4_reasons(reflection.weeklyQ4_reasons || "");
      setWeeklyQ4_betterPrepared(reflection.weeklyQ4_betterPrepared || "");
      setMonthlyQ1_accomplished(reflection.monthlyQ1_accomplished || "");
      setMonthlyQ1_movedNeedle(reflection.monthlyQ1_movedNeedle || "");
      setMonthlyQ5_workEthic(reflection.monthlyQ5_workEthic || "");
      setMonthlyQ5_alignActions(reflection.monthlyQ5_alignActions || "");
      setMonthlyQ6_emotionsInTrades(reflection.monthlyQ6_emotionsInTrades || "");
      setMonthlyQ6_emotionsMissed(reflection.monthlyQ6_emotionsMissed || "");
      setMonthlyQ10_otherAreas(reflection.monthlyQ10_otherAreas || "");
      setMonthlyQ10_grateful(reflection.monthlyQ10_grateful || "");
      setMonthlyQ11_reason(reflection.monthlyQ11_reason || "");
      setMonthlyQ11_protect(reflection.monthlyQ11_protect || "");
      setMonthlyQ12_reason(reflection.monthlyQ12_reason || "");
      setMonthlyQ12_protect(reflection.monthlyQ12_protect || "");
      setMonthlyQ13_reason(reflection.monthlyQ13_reason || "");
      setMonthlyQ13_protect(reflection.monthlyQ13_protect || "");
      setQuarterlyQ9_achieved(reflection.quarterlyQ9_achieved || "");
      setQuarterlyQ9_reasons(reflection.quarterlyQ9_reasons || "");
      setQuarterlyQ10_workEthic(reflection.quarterlyQ10_workEthic || "");
      setQuarterlyQ10_alignActions(reflection.quarterlyQ10_alignActions || "");
      setQuarterlyQ14_otherAreas(reflection.quarterlyQ14_otherAreas || "");
      setQuarterlyQ14_grateful(reflection.quarterlyQ14_grateful || "");
      switch (reflection.typeOfReview) {
        case "Weekly":
          setWeekly("Weekly");
          break;
        case "Monthly":
          setMonthly("Monthly");
          break;
        case "Annual":
          setAnnual("Annual");
          break;
        case "Q1":
          setQ1("Q1");
          break;
        case "Q2":
          setQ2("Q2");
          break;
        case "Q3":
          setQ3("Q3");
          break;
        case "Q4":
          setQ4("Q4");
          break;
        default:
          break;
      }
      setFinidng(false);
    }
  };
  useEffect(() => {
    localStorage.setItem("reflection-saved", true);
    findReflection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeReflection = (direction, pass) => {
    let next;
    let back;

    let array = pass;
    if (pass.length > 1) {
      // Filter to only reflections of the same type as current
      const currentType = SingleReflection?.typeOfReview;
      if (currentType) {
        // Group Q1, Q2, Q3, Q4 together as "Quarterly"
        const isQuarterly = ["Q1", "Q2", "Q3", "Q4"].includes(currentType);
        if (isQuarterly) {
          array = array.filter((reflection) =>
            ["Q1", "Q2", "Q3", "Q4"].includes(reflection.typeOfReview)
          );
        } else {
          array = array.filter((reflection) => reflection.typeOfReview === currentType);
        }
      }

      array = array.sort((a, b) => {
        return moment(a.date).diff(b.date);
      });

      const foundIndex = array.findIndex((x) => x._id === id);

      next = foundIndex + 1;
      back = foundIndex - 1;

      if (foundIndex === 0) {
        back = array.length - 1;
      }
      if (foundIndex + 1 === array.length) {
        next = 0;
      }

      if (direction === "Next") {
        setTypeOfReview(array[next]?.typeOfReview);
        switch (array[next]?.typeOfReview) {
          case "Weekly":
            setWeekly("Weekly");
            setMonthly("");
            setAnnual("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Monthly":
            setMonthly("Monthly");
            setAnnual("");
            setWeekly("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Annual":
            setAnnual("Annual");
            setMonthly("");
            setWeekly("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Q1":
            setQ1("Q1");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Q2":
            setQ2("Q2");
            setQ1("");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            setQ3("");
            setQ4("");
            break;
          case "Q3":
            setQ3("Q3");
            setQ2("Q2");
            setQ1("");
            setMonthly("");
            setWeekly("");
            setAnnual("");
            setQ4("");
            break;
          case "Q4":
            setQ4("Q4");
            setQ3("Q3");
            setQ2("Q2");
            setQ1("");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            break;
          default:
            break;
        }
        setId(array[next]?._id);
        localStorage.setItem("reflection-to-view", array[next]?._id);
        setQuestion_1(array[next]?.question_1);
        setQuestion_2(array[next]?.question_2);
        setQuestion_3(array[next]?.question_3);
        setQuestion_4(array[next]?.question_4);
        setQuestion_5(array[next]?.question_5);
        setQuestion_6(array[next]?.question_6);
        setQuestion_7(array[next]?.question_7);
        setQuestion_8(array[next]?.question_8);
        setQuestion_9(array[next]?.question_9);
        setQuestion_10(array[next]?.question_10);
        setQuestion_11(array[next]?.question_11);
        setQuestion_12(array[next]?.question_12);
        setQuestion_13(array[next]?.question_13);
        setQuestion_14(array[next]?.question_14);
        setQuestion_15(array[next]?.question_15);
        setQuestion_16(array[next]?.question_16);
        setQuestion_17(array[next]?.question_17);
        setQuestion_18(array[next]?.question_18);
        setDate(array[next]?.date);
        // Load sub-sections for navigation
        setWeeklyQ1_wentWell(array[next]?.weeklyQ1_wentWell || "");
        setWeeklyQ1_proudOf(array[next]?.weeklyQ1_proudOf || "");
        setWeeklyQ2_didntGoWell(array[next]?.weeklyQ2_didntGoWell || "");
        setWeeklyQ2_mistakes(array[next]?.weeklyQ2_mistakes || "");
        setWeeklyQ2_learnings(array[next]?.weeklyQ2_learnings || "");
        setWeeklyQ3_emotionalDiscipline(array[next]?.weeklyQ3_emotionalDiscipline || "");
        setWeeklyQ3_followedRules(array[next]?.weeklyQ3_followedRules || "");
        setWeeklyQ4_missedTrades(array[next]?.weeklyQ4_missedTrades || "");
        setWeeklyQ4_reasons(array[next]?.weeklyQ4_reasons || "");
        setWeeklyQ4_betterPrepared(array[next]?.weeklyQ4_betterPrepared || "");
        setMonthlyQ1_accomplished(array[next]?.monthlyQ1_accomplished || "");
        setMonthlyQ1_movedNeedle(array[next]?.monthlyQ1_movedNeedle || "");
        setMonthlyQ5_workEthic(array[next]?.monthlyQ5_workEthic || "");
        setMonthlyQ5_alignActions(array[next]?.monthlyQ5_alignActions || "");
        setMonthlyQ6_emotionsInTrades(array[next]?.monthlyQ6_emotionsInTrades || "");
        setMonthlyQ6_emotionsMissed(array[next]?.monthlyQ6_emotionsMissed || "");
        setMonthlyQ10_otherAreas(array[next]?.monthlyQ10_otherAreas || "");
        setMonthlyQ10_grateful(array[next]?.monthlyQ10_grateful || "");
        setMonthlyQ11_reason(array[next]?.monthlyQ11_reason || "");
        setMonthlyQ11_protect(array[next]?.monthlyQ11_protect || "");
        setMonthlyQ12_reason(array[next]?.monthlyQ12_reason || "");
        setMonthlyQ12_protect(array[next]?.monthlyQ12_protect || "");
        setMonthlyQ13_reason(array[next]?.monthlyQ13_reason || "");
        setMonthlyQ13_protect(array[next]?.monthlyQ13_protect || "");
        setQuarterlyQ9_achieved(array[next]?.quarterlyQ9_achieved || "");
        setQuarterlyQ9_reasons(array[next]?.quarterlyQ9_reasons || "");
        setQuarterlyQ10_workEthic(array[next]?.quarterlyQ10_workEthic || "");
        setQuarterlyQ10_alignActions(array[next]?.quarterlyQ10_alignActions || "");
        setQuarterlyQ14_otherAreas(array[next]?.quarterlyQ14_otherAreas || "");
        setQuarterlyQ14_grateful(array[next]?.quarterlyQ14_grateful || "");
      } else if (direction === "Back") {
        setTypeOfReview(array[back]?.typeOfReview);
        switch (array[back]?.typeOfReview) {
          case "Weekly":
            setWeekly("Weekly");
            setMonthly("");
            setAnnual("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Monthly":
            setMonthly("Monthly");
            setAnnual("");
            setWeekly("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Annual":
            setAnnual("Annual");
            setMonthly("");
            setWeekly("");
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Q1":
            setQ1("Q1");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            setQ2("");
            setQ3("");
            setQ4("");
            break;
          case "Q2":
            setQ2("Q2");
            setQ1("");
            setAnnual("");
            setMonthly("");
            setWeekly("");
            setQ3("");
            setQ4("");
            break;
          case "Q3":
            setQ3("Q3");
            setQ2("Q2");
            setQ1("");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            setQ4("");
            break;
          case "Q4":
            setQ4("Q4");
            setQ3("Q3");
            setQ2("Q2");
            setQ1("");
            setMonthly("");
            setAnnual("");
            setWeekly("");
            break;
          default:
            break;
        }
        setId(array[back]?._id);
        localStorage.setItem("reflection-to-view", array[back]?._id);
        setQuestion_1(array[back]?.question_1);
        setQuestion_2(array[back]?.question_2);
        setQuestion_3(array[back]?.question_3);
        setQuestion_4(array[back]?.question_4);
        setQuestion_5(array[back]?.question_5);
        setQuestion_6(array[back]?.question_6);
        setQuestion_7(array[back]?.question_7);
        setQuestion_8(array[back]?.question_8);
        setQuestion_9(array[back]?.question_9);
        setQuestion_10(array[back]?.question_10);
        setQuestion_11(array[back]?.question_11);
        setQuestion_12(array[back]?.question_12);
        setQuestion_13(array[back]?.question_13);
        setQuestion_14(array[back]?.question_14);
        setQuestion_15(array[back]?.question_15);
        setQuestion_16(array[back]?.question_16);
        setQuestion_17(array[back]?.question_17);
        setQuestion_18(array[back]?.question_18);

        setDate(array[back]?.date);
        // Load sub-sections for back navigation
        setWeeklyQ1_wentWell(array[back]?.weeklyQ1_wentWell || "");
        setWeeklyQ1_proudOf(array[back]?.weeklyQ1_proudOf || "");
        setWeeklyQ2_didntGoWell(array[back]?.weeklyQ2_didntGoWell || "");
        setWeeklyQ2_mistakes(array[back]?.weeklyQ2_mistakes || "");
        setWeeklyQ2_learnings(array[back]?.weeklyQ2_learnings || "");
        setWeeklyQ3_emotionalDiscipline(array[back]?.weeklyQ3_emotionalDiscipline || "");
        setWeeklyQ3_followedRules(array[back]?.weeklyQ3_followedRules || "");
        setWeeklyQ4_missedTrades(array[back]?.weeklyQ4_missedTrades || "");
        setWeeklyQ4_reasons(array[back]?.weeklyQ4_reasons || "");
        setWeeklyQ4_betterPrepared(array[back]?.weeklyQ4_betterPrepared || "");
        setMonthlyQ1_accomplished(array[back]?.monthlyQ1_accomplished || "");
        setMonthlyQ1_movedNeedle(array[back]?.monthlyQ1_movedNeedle || "");
        setMonthlyQ5_workEthic(array[back]?.monthlyQ5_workEthic || "");
        setMonthlyQ5_alignActions(array[back]?.monthlyQ5_alignActions || "");
        setMonthlyQ6_emotionsInTrades(array[back]?.monthlyQ6_emotionsInTrades || "");
        setMonthlyQ6_emotionsMissed(array[back]?.monthlyQ6_emotionsMissed || "");
        setMonthlyQ10_otherAreas(array[back]?.monthlyQ10_otherAreas || "");
        setMonthlyQ10_grateful(array[back]?.monthlyQ10_grateful || "");
        setMonthlyQ11_reason(array[back]?.monthlyQ11_reason || "");
        setMonthlyQ11_protect(array[back]?.monthlyQ11_protect || "");
        setMonthlyQ12_reason(array[back]?.monthlyQ12_reason || "");
        setMonthlyQ12_protect(array[back]?.monthlyQ12_protect || "");
        setMonthlyQ13_reason(array[back]?.monthlyQ13_reason || "");
        setMonthlyQ13_protect(array[back]?.monthlyQ13_protect || "");
        setQuarterlyQ9_achieved(array[back]?.quarterlyQ9_achieved || "");
        setQuarterlyQ9_reasons(array[back]?.quarterlyQ9_reasons || "");
        setQuarterlyQ10_workEthic(array[back]?.quarterlyQ10_workEthic || "");
        setQuarterlyQ10_alignActions(array[back]?.quarterlyQ10_alignActions || "");
        setQuarterlyQ14_otherAreas(array[back]?.quarterlyQ14_otherAreas || "");
        setQuarterlyQ14_grateful(array[back]?.quarterlyQ14_grateful || "");
      }
    }
  };

  return !finding ? (
    <>
      <Box
        sx={{
          overflowY: "scroll",
          overflowX: "hidden",
          position: "absolute",
          top: "64px",
          left: "50%",
          transform: "translateX(-50%)",
          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
          width: "100%",
          maxWidth: "1920px",
          height: "calc(100vh - 64px)",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
          padding: { xs: "20px 30px 50px 30px", sm: "20px 30px 20px 30px" },
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "95%",
            margin: "1em auto 1em auto",
            gap: width <= 600 ? "0.75em" : "1em",
          }}
        >
          <div
            onClick={() => changeReflection("Back", auth.reflections)}
            onMouseEnter={() => setIsHoveringBack(true)}
            onMouseLeave={() => setIsHoveringBack(false)}
            style={{
              cursor: "pointer",
              padding: width <= 600 ? "10px 16px" : "8px 16px",
              borderRadius: "20px",
              backgroundColor: isHoveringBack
                ? theme[0] === "dark"
                  ? "rgba(237, 158, 14, 0.15)"
                  : "rgba(237, 158, 14, 0.1)"
                : theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              border: `2px solid ${
                isHoveringBack
                  ? "#ED9E0E"
                  : theme[0] === "dark"
                    ? "#2d2e33"
                    : "#e5e7eb"
              }`,
              color: isHoveringBack
                ? "#ED9E0E"
                : theme[0] === "dark"
                  ? "#9ca3af"
                  : "#6b7280",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              transform: isHoveringBack ? "translateY(-2px)" : "translateY(0)",
              textAlign: "center",
            }}
          >
            ← PREVIOUS
          </div>
          <div
            onMouseEnter={() => setIsHoveringNext(true)}
            onMouseLeave={() => setIsHoveringNext(false)}
            onClick={() => changeReflection("Next", auth.reflections)}
            style={{
              cursor: "pointer",
              padding: width <= 600 ? "10px 16px" : "8px 16px",
              borderRadius: "20px",
              backgroundColor: isHoveringNext
                ? theme[0] === "dark"
                  ? "rgba(237, 158, 14, 0.15)"
                  : "rgba(237, 158, 14, 0.1)"
                : theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              border: `2px solid ${
                isHoveringNext
                  ? "#ED9E0E"
                  : theme[0] === "dark"
                    ? "#2d2e33"
                    : "#e5e7eb"
              }`,
              color: isHoveringNext
                ? "#ED9E0E"
                : theme[0] === "dark"
                  ? "#9ca3af"
                  : "#6b7280",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              transform: isHoveringNext ? "translateY(-2px)" : "translateY(0)",
              textAlign: "center",
            }}
          >
            NEXT →
          </div>
        </div>
        <div
          style={{
            maxWidth: "95%",
            margin:
              hideTypeOf && trades.length === 0 && width <= 800
                ? "1em auto 3em auto"
                : hideTypeOf && trades.length === 0 && width > 800
                  ? "1em auto 3em auto"
                  : width <= 800
                    ? "1em auto 2em auto"
                    : width > 800
                      ? "1em auto 2em auto"
                      : "1.5em auto",
            padding: width <= 768 ? "16px 20px" : "20px 32px",
            borderRadius: "16px",
            backgroundColor:
              theme[0] === "dark"
                ? "rgba(45, 46, 51, 0.4)"
                : "rgba(249, 250, 251, 0.8)",
            border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
            boxShadow:
              theme[0] === "dark"
                ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                : "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: "26px",
                fontWeight: "600",
                letterSpacing: "0.5px",
                color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
              }}
            >
              {(() => {
                if (
                  auth.backtestingMode &&
                  SingleReflection?.typeOfReview === "Backtest"
                ) {
                  // Get the most recent backtest for the same user
                  const backtest = auth.backtests?.find(
                    (test) => test.user_id === SingleReflection.user_id,
                  );
                  return `${backtest?.testId || "Backtest"} Reflection`;
                }
                return weekly
                  ? "Weekly"
                  : monthly
                    ? "Monthly"
                    : Annual
                      ? "Annual"
                      : q1
                        ? "Q1 Jan-Mar"
                        : q2
                          ? "Q2 Apr-Jun"
                          : q3
                            ? "Q3 Jul-Sep"
                            : q4
                              ? "Q4 Oct-Dec"
                              : "New";
              })()}
            </span>

            {!(
              auth.backtestingMode ||
              localStorage.getItem("backtest-mode") === "true"
            ) && (
              <>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.7)"
                        : "rgba(26, 27, 30, 0.7)",
                  }}
                >
                  {weekly
                    ? moment(date).format("ddd Do MMM")
                    : monthly
                      ? moment(date).format("MMMM YYYY")
                      : Annual
                        ? moment(date).format("YYYY")
                        : q1
                          ? ""
                          : q2
                            ? ""
                            : q3
                              ? ""
                              : q4
                                ? ""
                                : moment(date).format("ddd Do MMM")}
                </span>

                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.5)"
                        : "rgba(26, 27, 30, 0.5)",
                  }}
                >
                  Reflection
                </span>
              </>
            )}
          </div>
        </div>
        <Grid
          container
          justifyContent="space-evenly"
          alignItems="center"
          sx={{
            margin: "0 auto",
            display: "flex",
            flexDirection: width <= 500 ? "column" : "row",
          }}
        >
          {!hideTypeOf ? (
            <Grid
              item
              xs={5}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 0 2.8em 0",
              }}
            >
              <FormControl>
                <FormLabel
                  sx={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    textAlign: "center",
                  }}
                  id=""
                >
                  Reflection Type
                </FormLabel>

                <RadioGroup
                  row
                  aria-labelledby="go-to"
                  defaultValue=""
                  sx={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                  }}
                  onChange={(e) => {
                    setTypeOfReview(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value="Weekly"
                    control={
                      <Radio
                        onChange={handleWeekly}
                        sx={{
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        }}
                      />
                    }
                    label="Weekly"
                  />
                  <FormControlLabel
                    value="Monthly"
                    control={
                      <Radio
                        onChange={handleMonthly}
                        sx={{
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        }}
                      />
                    }
                    label="Monthly"
                  />
                  <FormControlLabel
                    value="Annual"
                    control={
                      <Radio
                        onChange={handleAnnual}
                        sx={{
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        }}
                      />
                    }
                    label="Annual"
                  />

                  {moment().quarter() === 1 && (
                    <FormControlLabel
                      value="Q1"
                      control={
                        <Radio
                          onChange={handleQ1}
                          sx={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          }}
                        />
                      }
                      label="Q1"
                    />
                  )}
                  {moment().quarter() === 2 && (
                    <FormControlLabel
                      value="Q2"
                      control={
                        <Radio
                          onChange={handleQ2}
                          sx={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          }}
                        />
                      }
                      label="Q2"
                    />
                  )}

                  {moment().quarter() === 3 && (
                    <FormControlLabel
                      value="Q3"
                      control={
                        <Radio
                          onChange={handleQ3}
                          sx={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          }}
                        />
                      }
                      label="Q3"
                    />
                  )}

                  {moment().quarter() === 4 && (
                    <FormControlLabel
                      value="Q4"
                      control={
                        <Radio
                          onChange={handleQ4}
                          sx={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          }}
                        />
                      }
                      label="Q4"
                    />
                  )}
                </RadioGroup>
              </FormControl>

              {/* <CustomMultiTextField
                size="small"
                rows={3}
                variant="standard"
                // value={higherTimeframeName}
                // label={higherTimeframeName ? "" : "Enter Timeframe"}
                onChange={(e) => {
                  // setHigherTimeframeName(e.target.value);
                }}
                fontSize="20px"
                // disableUnderline={true}
              /> */}
            </Grid>
          ) : null}
          {trades.length === 0 && typeOfReview && hideTypeOf && (
            <Grid
              item
              xs={12}
              style={{ margin: "0 auto 3em auto", textAlign: "center" }}
            >
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1em",
                  justifyContent: "center",
                  order: width <= 500 ? 2 : 0,
                }}
              >
                {!(
                  auth.backtestingMode ||
                  localStorage.getItem("backtest-mode") === "true"
                ) && (
                  <FormControlLabel
                    sx={{
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    }}
                    control={<Switch />}
                    label={missedTrades ? "Missed Trades" : "Live Trades"}
                    checked={missedTrades}
                    onChange={handleMissedTrades}
                    name={"Missed Trades"}
                  />
                )}
                {!(
                  auth.backtestingMode ||
                  localStorage.getItem("backtest-mode") === "true"
                )}
              </Grid>
              <p
                style={{
                  color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                  fontSize: "16px",
                  fontWeight: "400",
                  letterSpacing: "0.3px",
                }}
              >
                {!(
                  auth.backtestingMode ||
                  localStorage.getItem("backtest-mode") === "true"
                ) &&
                  (!missedTrades
                    ? "You didn't take any trades"
                    : "No missed trades this")}{" "}
                {weekly
                  ? "week"
                  : monthly
                    ? "month"
                    : Annual
                      ? "year"
                      : q1
                        ? "quarter"
                        : q2
                          ? "quarter"
                          : q3
                            ? "quarter"
                            : q4
                              ? "quarter"
                              : ""}{" "}
                {auth.backtestingMode ||
                localStorage.getItem("backtest-mode") === "true" ? (
                  <>{SingleReflection?.testId || "Backtest"} Reflection</>
                ) : (
                  <>on {displayMode}</>
                )}
              </p>
            </Grid>
          )}
          {/* {changeDate && typeOfReview ? (
          <Grid item xs={2} style={{ margin: "0 0 2em 0" }}>
            <CustomDatePicker
              size="small"
              label="Calendar Event Date"
              value={date}
              onChange={(newValue) => {
                setChangeDate(false);
                setDate(newValue.$d);
              }}
            />
          </Grid>
        ) : null} */}

          {trades.length > 0 ? (
            <>
              <Grid
                style={{
                  margin: "0 auto 0.5em auto",
                  width: width <= 800 ? "100%" : "95%",
                  order: width <= 500 ? 1 : 0,
                }}
              >
                {/* Backtest Info Section */}
                {auth.backtestingMode &&
                  SingleReflection?.typeOfReview === "Backtest" && (
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 1em auto",
                        flexWrap: "wrap",
                        gap: "12px",
                      }}
                    >
                      {(() => {
                        const backtest = auth.backtests?.find(
                          (test) => test.user_id === SingleReflection.user_id,
                        );
                        if (backtest) {
                          return (
                            <div
                              style={{
                                textAlign: "center",
                                width: width <= 1024 ? "100%" : "380px",
                                borderRadius: "12px",
                                backgroundColor:
                                  theme[0] === "dark"
                                    ? "rgba(59, 130, 246, 0.08)"
                                    : "rgba(59, 130, 246, 0.04)",
                                border: `1px solid ${theme[0] === "dark" ? "#3b82f6" : "#93c5fd"}`,
                                overflow: "hidden",
                                transition: "all 0.3s ease",
                                margin: "0 auto 2.25em auto",
                              }}
                            >
                              <div
                                style={{
                                  padding: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      color:
                                        theme[0] === "dark"
                                          ? "#93c5fd"
                                          : "#3b82f6",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Duration:{" "}
                                    {(() => {
                                      const startDate = new Date(
                                        backtest.startDate,
                                      );
                                      const endDate = new Date(
                                        backtest.exitDate,
                                      );
                                      const months =
                                        (endDate.getFullYear() -
                                          startDate.getFullYear()) *
                                          12 +
                                        (endDate.getMonth() -
                                          startDate.getMonth());
                                      const totalDays =
                                        (endDate - startDate) /
                                        (1000 * 60 * 60 * 24);

                                      if (totalDays < 30) {
                                        return `${Math.round(totalDays)} Days`;
                                      } else if (totalDays < 60) {
                                        return "1 Month";
                                      } else {
                                        return `${Math.round(months)} Months`;
                                      }
                                    })()}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: "16px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        color:
                                          theme[0] === "dark"
                                            ? "#93c5fd"
                                            : "#3b82f6",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Start:{" "}
                                      {moment(backtest.startDate).format(
                                        "MMM D, YYYY",
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        color:
                                          theme[0] === "dark"
                                            ? "#93c5fd"
                                            : "#3b82f6",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      End:{" "}
                                      {moment(backtest.exitDate).format(
                                        "MMM D, YYYY",
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </Grid>
                  )}

                {/* All Badges Grid */}
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1em auto",
                    flexDirection: width <= 768 ? "column" : "row",
                    gap: width <= 768 ? "8px" : "8px",
                  }}
                >
                  {/* Risk Badges Group */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      order: width <= 768 ? 2 : 0,
                    }}
                  >
                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)",
                        border: "1px solid #26a65d",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        LOW RISK
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#26a65d",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? lowRiskPC
                          : displayMode === "Investor Capital"
                            ? lowRiskIC
                            : lowRiskPC + lowRiskIC}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(255, 165, 0, 0.15)"
                            : "rgba(255, 165, 0, 0.1)",
                        border: "1px solid #FFA500",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        HIGH RISK
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#FFA500",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? highRiskPC
                          : displayMode === "Investor Capital"
                            ? highRiskIC
                            : highRiskPC + highRiskIC}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                        border: "1px solid #f23645",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        INVALID
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#f23645",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? invalidPC
                          : displayMode === "Investor Capital"
                            ? invalidIC
                            : invalidPC + invalidIC}
                      </p>
                    </div>
                  </div>

                  {width > 768 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: "40px",
                        borderColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                        mx: 1,
                      }}
                    />
                  )}

                  {/* Outcome Badges Group */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      order: width <= 768 ? 3 : 0,
                    }}
                  >
                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)",
                        border: "1px solid #26a65d",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        WINS
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#26a65d",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? winsPC
                          : displayMode === "Investor Capital"
                            ? winsIC
                            : winsPC + winsIC}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                        border: "1px solid #f23645",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        LOSSES
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#f23645",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? lossPC
                          : displayMode === "Investor Capital"
                            ? lossIC
                            : lossPC + lossIC}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)",
                        border: "1px solid #ED9E0E",
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        BREAKEVENS
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#ED9E0E",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? bePC
                          : displayMode === "Investor Capital"
                            ? beIC
                            : bePC + beIC}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(156, 163, 175, 0.15)"
                            : "rgba(107, 114, 128, 0.1)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                        }`,
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        IN PROGRESS
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: 0,
                        }}
                      >
                        {displayMode === "Personal Capital"
                          ? inProgressPC
                          : displayMode === "Investor Capital"
                            ? inProgressIC
                            : inProgressPC + inProgressIC}
                      </p>
                    </div>
                  </div>

                  {width > 768 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: "40px",
                        borderColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                        mx: 1,
                      }}
                    />
                  )}

                  {/* Return Badges Group */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      order: width <= 768 ? 1 : 0,
                    }}
                  >
                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(156, 163, 175, 0.15)"
                            : "rgba(107, 114, 128, 0.1)",
                        border: `1px solid ${setItPercent(
                          displayMode,
                          showGross ? returnPercentPC : returnPercentPCNet,
                          showGross ? returnPercentIC : returnPercentICNet,
                        )}`,
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        RETURN %
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: setItPercent(
                            displayMode,
                            showGross ? returnPercentPC : returnPercentPCNet,
                            showGross ? returnPercentIC : returnPercentICNet,
                          ),
                          margin: 0,
                        }}
                      >
                        {showGross ? (
                          displayMode === "Personal Capital"
                            ? `${Math.abs(returnPercentPC.toFixed(2))}%`
                            : displayMode === "Investor Capital"
                              ? `${Math.abs(returnPercentIC.toFixed(2))}%`
                              : `${Math.abs((returnPercentPC + returnPercentIC).toFixed(2))}%`
                        ) : (
                          displayMode === "Personal Capital"
                            ? `${Math.abs(returnPercentPCNet.toFixed(2))}%`
                            : displayMode === "Investor Capital"
                              ? `${Math.abs(returnPercentICNet.toFixed(2))}%`
                              : `${Math.abs((returnPercentPCNet + returnPercentICNet).toFixed(2))}%`
                        )}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(156, 163, 175, 0.15)"
                            : "rgba(107, 114, 128, 0.1)",
                        border: `1px solid ${setItDollar(
                          displayMode,
                          showGross ? returnDollarPersonalGross : returnDollarPersonalNet,
                          showGross ? returnDollarInvestorGross : returnDollarInvestorNet,
                        )}`,
                        minWidth: "70px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.6px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 1px 0",
                        }}
                      >
                        RETURN $
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: setItDollar(
                            displayMode,
                            showGross ? returnDollarPersonalGross : returnDollarPersonalNet,
                            showGross ? returnDollarInvestorGross : returnDollarInvestorNet,
                          ),
                          margin: 0,
                        }}
                      >
                        {showGross ? (
                          displayMode === "Personal Capital"
                            ? `${getCurrency()}${Math.abs(returnDollarPersonalGross.toFixed(2))}`
                            : displayMode === "Investor Capital"
                              ? `${getCurrency()}${Math.abs(returnDollarInvestorGross.toFixed(2))}`
                              : `${Math.abs((returnDollarPersonalGross + returnDollarInvestorGross).toFixed(2))}`
                        ) : (
                          displayMode === "Personal Capital"
                            ? `${getCurrency()}${Math.abs(returnDollarPersonalNet.toFixed(2))}`
                            : displayMode === "Investor Capital"
                              ? `${getCurrency()}${Math.abs(returnDollarInvestorNet.toFixed(2))}`
                              : `${Math.abs((returnDollarPersonalNet + returnDollarInvestorNet).toFixed(2))}`
                        )}
                      </p>
                    </div>
                  </div>
                </Grid>

                <DataGrid
                  sx={{
                    width: "100%",
                    border: `1px solid ${theme[0] === "dark" ? "#4a4b50" : "#d1d5db"}`,
                    height:
                      getCurrentReviewType() === "Annual"
                        ? (width <= 600 ? "700px" : "800px")
                        : (getCurrentReviewType() === "Quarterly" ||
                           getCurrentReviewType() === "Q1" ||
                           getCurrentReviewType() === "Q2" ||
                           getCurrentReviewType() === "Q3" ||
                           getCurrentReviewType() === "Q4")
                          ? (width <= 600 ? "500px" : "600px")
                          : getCurrentReviewType() === "Monthly"
                            ? (width <= 600 ? "450px" : "500px")
                            : (width <= 600 ? "350px" : "400px"),

                    "& .MuiSvgIcon-root": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
                      borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                      minHeight: "40px !important",
                      maxHeight: "40px !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      color: theme[0] === "dark" ? "#c9d1d9" : "#374151",
                      fontWeight: "600",
                      padding: "0 8px",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        height: "12px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor:
                          theme[0] === "dark" ? "rgba(60, 62, 66)" : "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor:
                          theme[0] === "dark" ? "rgba(120, 120, 120)" : "#888",
                        borderRadius: "6px",
                        "&:hover": {
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(140, 140, 140)"
                              : "#555",
                        },
                      },
                    },
                    backgroundColor:
                      theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                    color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    "& .MuiDataGrid-cell": {
                      cursor: "pointer",
                      borderBottom: "none",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiTablePagination-actions": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-row": {
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                    },
                  }}
                  rows={trades}
                  columns={columnsAll(
                    theme,
                    throwMessage,
                    null, // idTradeGenerator - not needed for reflections
                    runDurationCal,
                    null, // handleOpenJournal - handled via setSingleJournalView below
                    auth,
                    null, // calculateFields - not needed for reflections
                    selectedAccountId,
                    setSingleJournalView,
                    setSingleReflectionView,
                    setOpenJournal,
                    setSingleForecastView,
                    handleCloseNewReflection,
                    trades
                  )}
                  autoPageSize
                  rowHeight={53}
                  // disableSelectionOnClick
                />

                {/* Query Controls - After DataGrid */}
                {weekly || monthly || Annual || q1 || q2 || q3 || q4 ? (
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "1em",
                      marginBottom: "1em",
                      justifyContent: "center",
                      order: width <= 500 ? 2 : 0,
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {!(
                      auth.backtestingMode ||
                      localStorage.getItem("backtest-mode") === "true"
                    ) && (
                      <FormControlLabel
                        sx={{
                          color: theme[0] === "dark" ? "#FCFCFF" : "black",
                        }}
                        control={<Switch />}
                        label={missedTrades ? "Missed Trades" : "Live Trades"}
                        checked={missedTrades}
                        onChange={handleMissedTrades}
                        name={"Missed Trades"}
                      />
                    )}
                    {!(
                      auth.backtestingMode ||
                      localStorage.getItem("backtest-mode") === "true"
                    )}
                  </Grid>
                ) : null}

                <Divider
                  sx={{
                    my: 3,
                    borderColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                  }}
                />
              </Grid>
            </>
          ) : null}

          <Grid
            container
            spacing={5}
            justifyContent="space-evenly"
            alignItems="center"
            sx={{
              minHeight: "100%",
              fontSize: "16px",
              order: width <= 500 ? 2 : 0,
            }}
          >
            {/* Section Headers and Additional Questions - Dynamic based on review type */}
            {(() => {
              const questions = getReflectionQuestions(
                getTimeframeText(),
                getCurrentReviewType(),
              );
              const reviewType = getCurrentReviewType();
              const elements = [];

              // Create mapping for question states to avoid eval()
              const questionStateMap = {
                1: question_1,
                2: question_2,
                3: question_3,
                4: question_4,
                5: question_5,
                6: question_6,
                7: question_7,
                8: question_8,
                9: question_9,
                10: question_10,
                11: question_11,
                12: question_12,
                13: question_13,
                14: question_14,
                15: question_15,
                16: question_16,
                17: question_17,
                18: question_18,
              };

              const renderQuestion = (i) => {
                const questionKey = `question_${i}`;
                if (questions[questionKey]) {
                  return (
                    <Grid
                      key={i}
                      item
                      xs={12}
                      style={{ marginTop: width <= 600 ? "1em" : "1em" }}
                    >
                      <Box
                        sx={{
                          padding: { xs: "20px", sm: "24px" },
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                fontStyle: "italic",
                                margin: "0 0 4px 0",
                              }}
                            >
                              <span
                                style={{
                                  color:
                                    theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                }}
                              >
                                Q{i})
                              </span>{" "}
                              <span
                                style={{
                                  color:
                                    theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                                }}
                              >
                                {questions[questionKey]}
                              </span>
                            </p>
                          </div>
                          <VoiceToText
                            onTranscript={(text) => {
                              const setterMap = {
                                1: setQuestion_1,
                                2: setQuestion_2,
                                3: setQuestion_3,
                                4: setQuestion_4,
                                5: setQuestion_5,
                                6: setQuestion_6,
                                7: setQuestion_7,
                                8: setQuestion_8,
                                9: setQuestion_9,
                                10: setQuestion_10,
                                11: setQuestion_11,
                                12: setQuestion_12,
                                13: setQuestion_13,
                                14: setQuestion_14,
                                15: setQuestion_15,
                                16: setQuestion_16,
                                17: setQuestion_17,
                                18: setQuestion_18,
                              };
                              setterMap[i](text);
                            }}
                            currentValue={questionStateMap[i] || ""}
                          />
                        </div>
                        {/* Render sub-sections for specific questions */}
                        {reviewType === "Weekly" && i === 1 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              This week:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ1_wentWell)}
                              variant="standard"
                              value={weeklyQ1_wentWell}
                              onChange={(e) => setWeeklyQ1_wentWell(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Proud of:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ1_proudOf)}
                              variant="standard"
                              value={weeklyQ1_proudOf}
                              onChange={(e) => setWeeklyQ1_proudOf(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Weekly" && i === 2 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              This week:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ2_didntGoWell)}
                              variant="standard"
                              value={weeklyQ2_didntGoWell}
                              onChange={(e) => setWeeklyQ2_didntGoWell(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Mistakes:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ2_mistakes)}
                              variant="standard"
                              value={weeklyQ2_mistakes}
                              onChange={(e) => setWeeklyQ2_mistakes(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Learn:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ2_learnings)}
                              variant="standard"
                              value={weeklyQ2_learnings}
                              onChange={(e) => setWeeklyQ2_learnings(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Weekly" && i === 3 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Emotional discipline:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ3_emotionalDiscipline)}
                              variant="standard"
                              value={weeklyQ3_emotionalDiscipline}
                              onChange={(e) => setWeeklyQ3_emotionalDiscipline(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Followed my rules:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ3_followedRules)}
                              variant="standard"
                              value={weeklyQ3_followedRules}
                              onChange={(e) => setWeeklyQ3_followedRules(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Weekly" && i === 4 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Missed trades:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ4_missedTrades)}
                              variant="standard"
                              value={weeklyQ4_missedTrades}
                              onChange={(e) => setWeeklyQ4_missedTrades(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Reasons:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ4_reasons)}
                              variant="standard"
                              value={weeklyQ4_reasons}
                              onChange={(e) => setWeeklyQ4_reasons(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Better prepared next week:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(weeklyQ4_betterPrepared)}
                              variant="standard"
                              value={weeklyQ4_betterPrepared}
                              onChange={(e) => setWeeklyQ4_betterPrepared(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Monthly" && i === 1 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              This month:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ1_accomplished)}
                              variant="standard"
                              value={monthlyQ1_accomplished}
                              onChange={(e) => setMonthlyQ1_accomplished(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Move the needle:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ1_movedNeedle)}
                              variant="standard"
                              value={monthlyQ1_movedNeedle}
                              onChange={(e) => setMonthlyQ1_movedNeedle(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Monthly" && i === 5 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Work ethic:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ5_workEthic)}
                              variant="standard"
                              value={monthlyQ5_workEthic}
                              onChange={(e) => setMonthlyQ5_workEthic(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Actions align:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ5_alignActions)}
                              variant="standard"
                              value={monthlyQ5_alignActions}
                              onChange={(e) => setMonthlyQ5_alignActions(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Monthly" && i === 6 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Emotions in trades:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ6_emotionsInTrades)}
                              variant="standard"
                              value={monthlyQ6_emotionsInTrades}
                              onChange={(e) => setMonthlyQ6_emotionsInTrades(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                             Emotions in missed trades:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ6_emotionsMissed)}
                              variant="standard"
                              value={monthlyQ6_emotionsMissed}
                              onChange={(e) => setMonthlyQ6_emotionsMissed(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Monthly" && i === 10 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Other areas of my life:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ10_otherAreas)}
                              variant="standard"
                              value={monthlyQ10_otherAreas}
                              onChange={(e) => setMonthlyQ10_otherAreas(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Things I am grateful for:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(monthlyQ10_grateful)}
                              variant="standard"
                              value={monthlyQ10_grateful}
                              onChange={(e) => setMonthlyQ10_grateful(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : reviewType === "Monthly" && (i === 11 || i === 12 || i === 13) ? (
                          (() => {
                            const subFieldMap = {
                              11: { reason: monthlyQ11_reason, protect: monthlyQ11_protect, setReason: setMonthlyQ11_reason, setProtect: setMonthlyQ11_protect },
                              12: { reason: monthlyQ12_reason, protect: monthlyQ12_protect, setReason: setMonthlyQ12_reason, setProtect: setMonthlyQ12_protect },
                              13: { reason: monthlyQ13_reason, protect: monthlyQ13_protect, setReason: setMonthlyQ13_reason, setProtect: setMonthlyQ13_protect },
                            };
                            const fields = subFieldMap[i];
                            return (
                              <>
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                    margin: "0 0 4px 0",
                                  }}
                                >
                                  Common reason:
                                </p>
                                <CustomMultiTextField
                                  size="small"
                                  rows={calculateRows(fields.reason)}
                                  variant="standard"
                                  value={fields.reason}
                                  onChange={(e) => fields.setReason(e.target.value)}
                                  disableUnderline={true}
                                  fontSize={"16px"}
                                />
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                    margin: "12px 0 4px 0",
                                  }}
                                >
                                  Protect against:
                                </p>
                                <CustomMultiTextField
                                  size="small"
                                  rows={calculateRows(fields.protect)}
                                  variant="standard"
                                  value={fields.protect}
                                  onChange={(e) => fields.setProtect(e.target.value)}
                                  disableUnderline={true}
                                  fontSize={"16px"}
                                />
                              </>
                            );
                          })()
                        ) : (reviewType === "Q1" || reviewType === "Q2" || reviewType === "Q3" || reviewType === "Q4") && i === 9 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Achieve everything:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ9_achieved)}
                              variant="standard"
                              value={quarterlyQ9_achieved}
                              onChange={(e) => setQuarterlyQ9_achieved(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Reasons:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ9_reasons)}
                              variant="standard"
                              value={quarterlyQ9_reasons}
                              onChange={(e) => setQuarterlyQ9_reasons(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : (reviewType === "Q1" || reviewType === "Q2" || reviewType === "Q3" || reviewType === "Q4") && i === 10 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Work ethic:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ10_workEthic)}
                              variant="standard"
                              value={quarterlyQ10_workEthic}
                              onChange={(e) => setQuarterlyQ10_workEthic(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Truly align:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ10_alignActions)}
                              variant="standard"
                              value={quarterlyQ10_alignActions}
                              onChange={(e) => setQuarterlyQ10_alignActions(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : (reviewType === "Q1" || reviewType === "Q2" || reviewType === "Q3" || reviewType === "Q4") && i === 14 ? (
                          <>
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "0 0 4px 0",
                              }}
                            >
                              Other areas of my life:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ14_otherAreas)}
                              variant="standard"
                              value={quarterlyQ14_otherAreas}
                              onChange={(e) => setQuarterlyQ14_otherAreas(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                margin: "12px 0 4px 0",
                              }}
                            >
                              Things I am grateful for:
                            </p>
                            <CustomMultiTextField
                              size="small"
                              rows={calculateRows(quarterlyQ14_grateful)}
                              variant="standard"
                              value={quarterlyQ14_grateful}
                              onChange={(e) => setQuarterlyQ14_grateful(e.target.value)}
                              disableUnderline={true}
                              fontSize={"16px"}
                            />
                          </>
                        ) : (
                          <CustomMultiTextField
                            size="small"
                            rows={calculateRows(questionStateMap[i] || "")}
                            variant="standard"
                            value={questionStateMap[i] || ""}
                            onChange={(e) => {
                              const setterMap = {
                                1: setQuestion_1,
                                2: setQuestion_2,
                                3: setQuestion_3,
                                4: setQuestion_4,
                                5: setQuestion_5,
                                6: setQuestion_6,
                                7: setQuestion_7,
                                8: setQuestion_8,
                                9: setQuestion_9,
                                10: setQuestion_10,
                                11: setQuestion_11,
                                12: setQuestion_12,
                                13: setQuestion_13,
                                14: setQuestion_14,
                                15: setQuestion_15,
                                16: setQuestion_16,
                                17: setQuestion_17,
                                18: setQuestion_18,
                              };
                              setterMap[i](e.target.value);
                            }}
                            disableUnderline={true}
                            fontSize={"16px"}
                          />
                        )}
                      </Box>
                    </Grid>
                  );
                }
                return null;
              };

              // Add section headers and questions based on review type
              if (reviewType === "Monthly") {
                // Part 1: General Reflections Accordion
                const part1Questions = [];
                for (let i = 1; i <= 10; i++) {
                  part1Questions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="part1-accordion-grid">
                    <Accordion
                      key="part1-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                        marginBottom: "1em",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 1: General Reflections
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {part1Questions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );

                // Part 2: How to Miss Less Trades Accordion
                const part2Questions = [];
                for (let i = 11; i <= 13; i++) {
                  part2Questions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="part2-accordion-grid">
                    <Accordion
                      key="part2-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 2: How to Miss Less Trades Next Month
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {part2Questions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );
              } else if (
                reviewType === "Quarterly" ||
                reviewType === "Q1" ||
                reviewType === "Q2" ||
                reviewType === "Q3" ||
                reviewType === "Q4"
              ) {
                // Part 1: Technical SWOT Analysis Accordion
                const part1Questions = [];
                for (let i = 1; i <= 4; i++) {
                  part1Questions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="quarterly-part1-accordion-grid">
                    <Accordion
                      key="quarterly-part1-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                        marginBottom: "1em",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 1: Technical SWOT Analysis
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {part1Questions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );

                // Part 2: Psychological SWOT Analysis Accordion
                const part2Questions = [];
                for (let i = 5; i <= 8; i++) {
                  part2Questions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="quarterly-part2-accordion-grid">
                    <Accordion
                      key="quarterly-part2-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                        marginBottom: "1em",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 2: Psychological SWOT Analysis
                       
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {part2Questions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );

                // Part 3: Deep Dive Performance Review Accordion
                const part3Questions = [];
                for (let i = 9; i <= 14; i++) {
                  part3Questions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="quarterly-part3-accordion-grid">
                    <Accordion
                      key="quarterly-part3-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 3: Deep Dive Performance Review
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {part3Questions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );
              } else if (reviewType === "Annual") {
                // Annual review - no questions to render
                // Skip rendering any elements
              } else {
                // For Weekly - render only 5 questions in an accordion
                const weeklyQuestions = [];
                for (let i = 1; i <= 5; i++) {
                  weeklyQuestions.push(renderQuestion(i));
                }
                elements.push(
                  <Grid item xs={12} sm={11} key="weekly-accordion-grid">
                    <Accordion
                      key="weekly-accordion"
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        boxShadow: "none",
                      }}
                    >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }} />}
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        "& .MuiAccordionSummary-content": {
                          margin: "0.5em 0",
                        },
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: "700",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Weekly Reflection Questions
                      </div>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                        paddingTop: 0,
                      }}
                    >
                      <Grid container spacing={0}>
                        {weeklyQuestions}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  </Grid>
                );
              }

              return elements;
            })()}

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                margin: "0 0 1em 0",
                order: width <= 500 ? 3 : 0,
              }}
            >
              <Tooltip title="Click to Delete">
                <div
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 18px",
                    borderRadius: "24px",
                    cursor: "pointer",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(242, 54, 69, 0.12)"
                        : "rgba(242, 54, 69, 0.08)",
                    border: "2px solid #f23645",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(242, 54, 69, 0.2)"
                        : "rgba(242, 54, 69, 0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(242, 54, 69, 0.12)"
                        : "rgba(242, 54, 69, 0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <DeleteOutlinedIcon
                    sx={{
                      color: "#f23645",
                      fontSize: "24px",
                    }}
                  />
                  <span
                    style={{
                      color: "#f23645",
                      fontWeight: "600",
                      fontSize: "14px",
                      letterSpacing: "0.5px",
                      display: width < 768 ? "none" : "inline",
                    }}
                  >
                    DELETE
                  </span>
                </div>
              </Tooltip>

              <Tooltip title={isSharing ? "Generating..." : "Share Reflection"}>
                <div
                  onClick={handleShare}
                  onMouseEnter={() => setIsHoveringShare(true)}
                  onMouseLeave={(e) => {
                    setIsHoveringShare(false);
                    if (!isSharing) {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.12)"
                          : "rgba(107, 114, 128, 0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 18px",
                    borderRadius: "24px",
                    cursor: isSharing ? "not-allowed" : "pointer",
                    backgroundColor:
                      isHoveringShare || isSharing
                        ? theme[0] === "dark"
                          ? "rgba(59, 130, 246, 0.15)"
                          : "rgba(59, 130, 246, 0.1)"
                        : theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.12)"
                          : "rgba(107, 114, 128, 0.08)",
                    border: `2px solid ${
                      isHoveringShare || isSharing
                        ? "#3B82F6"
                        : theme[0] === "dark"
                          ? "#9ca3af"
                          : "#6b7280"
                    }`,
                    color:
                      isHoveringShare || isSharing
                        ? "#3B82F6"
                        : theme[0] === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                    transition: "all 0.3s ease",
                    opacity: isSharing ? 0.6 : 1,
                  }}
                >
                  <ShareIcon
                    sx={{
                      color:
                        isHoveringShare || isSharing
                          ? "#3B82F6"
                          : theme[0] === "dark"
                            ? "#9ca3af"
                            : "#6b7280",
                      fontSize: "24px",
                    }}
                  />
                  <span
                    style={{
                      color:
                        isHoveringShare || isSharing
                          ? "#3B82F6"
                          : theme[0] === "dark"
                            ? "#9ca3af"
                            : "#6b7280",
                      fontWeight: "600",
                      fontSize: "14px",
                      letterSpacing: "0.5px",
                      display: width < 768 ? "none" : "inline",
                    }}
                  >
                    {isSharing ? "GENERATING..." : "SHARE"}
                  </span>
                </div>
              </Tooltip>

              <Tooltip title="Close">
                <div
                  onClick={() => goBack()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 18px",
                    borderRadius: "24px",
                    cursor: "pointer",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.12)"
                        : "rgba(107, 114, 128, 0.08)",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                    }`,
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.2)"
                        : "rgba(107, 114, 128, 0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.12)"
                        : "rgba(107, 114, 128, 0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <CloseIcon
                    sx={{
                      color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                      fontSize: "24px",
                    }}
                  />
                  <span
                    style={{
                      color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                      fontWeight: "600",
                      fontSize: "14px",
                      letterSpacing: "0.5px",
                      display: width < 768 ? "none" : "inline",
                    }}
                  >
                    CLOSE
                  </span>
                </div>
              </Tooltip>

              <Tooltip title={formSubmitted ? "Saving..." : "Save Changes"}>
                <div
                  onClick={() => {
                    if (!formSubmitted) handleSave();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 18px",
                    borderRadius: "24px",
                    cursor: formSubmitted ? "not-allowed" : "pointer",
                    backgroundColor:
                      theme[0] === "dark"
                        ? formSubmitted
                          ? "rgba(156, 163, 175, 0.12)"
                          : "rgba(38, 166, 93, 0.12)"
                        : formSubmitted
                          ? "rgba(107, 114, 128, 0.08)"
                          : "rgba(38, 166, 93, 0.08)",
                    border: `2px solid ${formSubmitted ? "#9ca3af" : "#26a65d"}`,
                    transition: "all 0.3s ease",
                    opacity: formSubmitted ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!formSubmitted) {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.2)"
                          : "rgba(38, 166, 93, 0.15)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formSubmitted) {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(38, 166, 93, 0.12)"
                          : "rgba(38, 166, 93, 0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  <SaveIcon
                    sx={{
                      color: formSubmitted ? "#9ca3af" : "#26a65d",
                      fontSize: "24px",
                    }}
                  />
                  <span
                    style={{
                      color: formSubmitted ? "#9ca3af" : "#26a65d",
                      fontWeight: "600",
                      fontSize: "14px",
                      letterSpacing: "0.5px",
                      display: width < 768 ? "none" : "inline",
                    }}
                  >
                    SAVE
                  </span>
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteReflections}
        title="Delete Reflection"
        message="Are you sure you want to delete this reflection?"
      />
    </>
  ) : (
    "Loading"
  );
};

export default SingleReflection;
